import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Text } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { useIsOnReviewStep, useMultiplexDetails } from '../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useFeedOverrideSectionEnabled } from '../../../../hooks/useFeedOverrideSectionEnabled';
import accessUtils from '../../../../utils/accessUtils';

import { PHOTO_UPLOAD_FROM_COMPUTER_TEXT } from '../../../../constants/PhotoUploadConstants';

import PhotoGallery from './components/PhotoGallery';
import PhotoUpload from './components/PhotoUpload';

import type { ReactNode } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';

/** Server photos are authoritative; keep local-only photos only while a pending upload has not refetched yet. */
const mergePhotos = (
  localPhotos: Array<PhotoProps>,
  serverPhotos: Array<PhotoProps>,
  pendingUploadPhotoIds: Set<string>,
): Array<PhotoProps> => {
  const serverIds = new Set(serverPhotos.map((photo) => photo.id));
  const optimisticPhotos = localPhotos.filter(
    (photo) => !serverIds.has(photo.id) && pendingUploadPhotoIds.has(photo.id),
  );

  return [...serverPhotos, ...optimisticPhotos];
};

type Props = {
  /** Called with the variant that fits where it renders: `tile` in the photo grid, `panel` beside the dropzone. */
  renderPhoneUpload?: (variant: 'tile' | 'panel') => ReactNode;
  /** `tiles` pairs compact tiles when there are no photos yet; `panels` keeps the full-size dropzone beside a panel. */
  emptyStateLayout?: 'tiles' | 'panels';
};

/**
 * Property photos for the create-listing wizard.
 *
 * During the multiplex wizard, photos upload to the multiplex flow, not the creation flow yet.
 * We read photos from multiplex details on wizard steps and from creation flow details on review.
 * Creation flow details do not include photos that only exist on the multiplex flow.
 *
 * We keep local photo state so new uploads show up right away after upload succeeds,
 * before the details query refetches. That is a simple optimistic update: PhotoUpload
 * calls onUploadSuccess, we append to photos, the gallery updates, then multiplex or
 * creation flow details refetch and the useEffect syncs server data back in.
 */
const PhotoUploadFormField = ({ renderPhoneUpload, emptyStateLayout = 'tiles' }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });

  const [photos, setPhotos] = useState<Array<PhotoProps>>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  /**
   * mergePhotos keeps local-only photos during optimistic uploads. Without this allowlist, a deleted photo
   * (in local state, gone from server) would be mistaken for a pending upload and re-appended on refetch.
   */
  const pendingUploadPhotoIdsRef = useRef<Set<string>>(new Set());
  const isFeed = listingDetails?.isFeed ?? false;
  const isUsingMultiplexPhotos = !isOnReviewStep && multiplexData?.propertyPhotos != null;
  /**
   * Hide the QR/phone upload while the gallery shows multiplex session photos, and for feed listings: the phone
   * flow's generateUploadUrl only accepts manual listings, so feed uploads must go through the dropzone.
   */
  const renderPhoneUploadToUse = isUsingMultiplexPhotos || isFeed ? undefined : renderPhoneUpload;

  /** Apply refetched photos from multiplex or listing details; drop pending-upload ids the server now has. */
  const syncPhotosFromServer = (serverPhotos: Array<PhotoProps>) => {
    for (const photo of serverPhotos) {
      pendingUploadPhotoIdsRef.current.delete(photo.id);
    }

    setPhotos((prev) => mergePhotos(prev, serverPhotos, pendingUploadPhotoIdsRef.current));
  };

  /** Load photos from multiplex details on wizard steps. */
  useEffect(() => {
    // Each parallel upload invalidates multiplex details; skip sync until the batch finishes
    // or placeholders and refetched photos render together (e.g. 3 photos + 3 placeholders).
    if (uploadingCount > 0) {
      return;
    }

    if (isUsingMultiplexPhotos && multiplexData?.propertyPhotos) {
      syncPhotosFromServer(multiplexData.propertyPhotos);
    }
  }, [isUsingMultiplexPhotos, multiplexData?.propertyPhotos, uploadingCount]);

  /** Load photos from creation flow details on review, or before multiplex details are available. */
  useEffect(() => {
    if (uploadingCount > 0) {
      return;
    }

    if (!isUsingMultiplexPhotos) {
      syncPhotosFromServer(listingDetails?.propertyPhotos ?? []);
    }
  }, [isUsingMultiplexPhotos, listingDetails?.propertyPhotos, uploadingCount]);

  /** Add uploaded photos to the gallery right away, before refetch finishes. */
  const handleUploadSuccess = (uploadedPhotos: Array<PhotoProps>) => {
    setUploadingCount(0);

    if (uploadedPhotos.length === 0) {
      return;
    }

    setPhotos((prev) => {
      const existingIds = new Set(prev.map((photo) => photo.id));
      const newPhotos = uploadedPhotos.filter((photo) => !existingIds.has(photo.id));

      if (newPhotos.length === 0) {
        return prev;
      }

      for (const photo of newPhotos) {
        pendingUploadPhotoIdsRef.current.add(photo.id);
      }

      return [...prev, ...newPhotos];
    });
  };

  const handlePhotoDeleted = (photoId: string) => {
    pendingUploadPhotoIdsRef.current.delete(photoId);
  };

  const isMediaOverrideEnabled = useFeedOverrideSectionEnabled(listingAlias, FeedOverrideSection.MEDIA);
  const isFeedOverridesEditAllowed = isFeed && isMediaOverrideEnabled && accessUtils.canAccessFeedOverrides();
  const isEditAllowed = !isFeed || isFeedOverridesEditAllowed;

  if (!photos?.length && !isEditAllowed) {
    return (
      <Box css={{ width: '100%' }} data-testid="PhotoUploadFormField">
        <Text css={{ marginTop: '200', flex: '1 0 fit-content' }}>No photos available</Text>
      </Box>
    );
  }

  const uploadProps = {
    photoContext: { type: 'property', listingAlias } as const,
    setUploadingCount,
    uploadingCount,
    onUploadSuccess: handleUploadSuccess,
  };

  const renderEmptyState = () => {
    if (!isEditAllowed) {
      return null;
    }

    if (renderPhoneUploadToUse === undefined) {
      return <PhotoUpload {...uploadProps} />;
    }

    if (emptyStateLayout === 'panels') {
      return (
        <Box css={{ display: 'flex', flexDirection: { base: 'column', md: 'row' }, columnGap: '400', rowGap: '400' }}>
          {/* hasFixedEmptySize: the default collapses the dropzone to 100x100 below 480px, where the panel is full width. */}
          <PhotoUpload {...uploadProps} smallDisplayText={PHOTO_UPLOAD_FROM_COMPUTER_TEXT} hasFixedEmptySize={false} />
          {renderPhoneUploadToUse('panel')}
        </Box>
      );
    }

    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          columnGap: '400',
          rowGap: '400',
        }}
      >
        <PhotoUpload
          {...uploadProps}
          isSmallDropzone={true}
          smallDisplayText={PHOTO_UPLOAD_FROM_COMPUTER_TEXT}
          hasFixedEmptySize={false}
        />
        {renderPhoneUploadToUse('tile')}
      </Box>
    );
  };

  return (
    <Box css={{ width: '100%' }} data-testid="PhotoUploadFormField">
      {photos.length > 0 || uploadingCount > 0 ? (
        <PhotoGallery
          phoneUploadTrigger={renderPhoneUploadToUse?.('tile')}
          photoContext={{ type: 'property', listingAlias }}
          photos={photos}
          setPhotos={setPhotos}
          uploadingCount={uploadingCount}
          setUploadingCount={setUploadingCount}
          onUploadSuccess={handleUploadSuccess}
          onPhotoDeleted={handlePhotoDeleted}
          isUploadAllowed={isEditAllowed}
        />
      ) : (
        renderEmptyState()
      )}
    </Box>
  );
};

export default PhotoUploadFormField;
